import React, { useState, useEffect } from "react";
import { Navbar, Footer } from "../../@components";
import SEO from "../../components/SEO";
import { motion } from "framer-motion";
import Papa from "papaparse";
// Add styles to override Navbar positioning
const navbarOverrideStyles = `
  #root > div > section:first-child nav > nav {
    position: relative !important;
    top: 0 !important;
  }
`;

const CSVViewer = ({
  compact = false,
  csvUrl = "/delivery_data.csv",
  compactTitle = "Solution Viewer",
  compactDescription = "Explore our CSV deliverables.",
  compactRowsPerPageOptions = [5, 15, 30, 50, 100],
}) => {
  const [tableData, setTableData] = useState([]); // State to store the parsed CSV data
  const [loading, setLoading] = useState(true); // State to manage loading status
  const [error, setError] = useState(null); // State to handle any errors
  const [rowsPerPage, setRowsPerPage] = useState(5); // Add state for rows per page
  const [currentPage, setCurrentPage] = useState(1); // Add state for current page

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(csvUrl);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const csvText = await response.text();

        Papa.parse(csvText, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          complete: (results) => {
            setTableData(results.data);
            setLoading(false);
          },
          error: (err) => {
            setError(err);
            setLoading(false);
          },
        });
      } catch (e) {
        setError(e);
        setLoading(false);
      }
    };

    fetchData();
  }, [csvUrl]);

  // Extract headers dynamically for the table
  const headers = tableData.length > 0 ? Object.keys(tableData[0]) : [];

  // Calculate pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = tableData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(tableData.length / rowsPerPage);

  if (compact) {
    return (
      <section className="bg-white rounded-3xl p-4 md:p-6">
        <div className="container mx-auto">
          <h3 className="text-2xl md:text-3xl font-light text-black mb-2">
            {compactTitle}
          </h3>
          <p className="text-gray-600 text-sm md:text-base mb-4">
            {compactDescription}
          </p>

          {loading && (
            <div className="text-center text-lg text-gray-600">
              Loading deliverables data...
            </div>
          )}

          {error && (
            <div className="text-center text-lg text-red-600">
              Error: {error.message}. Please check your CSV file.
            </div>
          )}

          {!loading && !error && tableData.length === 0 && (
            <div className="text-center text-lg text-gray-600">
              No deliverable data found.
            </div>
          )}

          {!loading && !error && tableData.length > 0 && (
            <div className="flex flex-col gap-4">
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full">
                  <thead className="bg-[#112917]">
                    <tr>
                      {headers.map((header, index) => (
                        <th
                          key={index}
                          className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider border-b border-r last:border-r-0 border-white/20"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentRows.map((row, rowIndex) => (
                      <tr
                        key={rowIndex}
                        className={
                          rowIndex % 2 === 0 ? "bg-gray-50" : "bg-white"
                        }
                      >
                        {headers.map((header, colIndex) => (
                          <td
                            key={`${rowIndex}-${colIndex}`}
                            className="px-6 py-4 text-sm text-gray-800 border-b border-r last:border-r-0 border-gray-100 break-words"
                          >
                            {row[header] !== null && row[header] !== undefined
                              ? row[header].toString()
                              : ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between items-center mt-4">
                <div className="text-sm text-gray-600">
                  Showing {indexOfFirstRow + 1} to{" "}
                  {Math.min(indexOfLastRow, tableData.length)} of{" "}
                  {tableData.length} entries
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-sm text-gray-700">
                    Rows per page:
                  </label>
                  <select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded px-2 py-1.5 bg-white text-gray-900 cursor-pointer appearance-none focus:outline-none focus:border-[#112917] focus:ring-1 focus:ring-[#112917]"
                    style={{
                      WebkitAppearance: "menulist",
                      MozAppearance: "menulist",
                    }}
                  >
                    {compactRowsPerPageOptions.map((value) => (
                      <option
                        key={value}
                        value={value}
                        className="bg-white text-gray-900"
                      >
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={currentPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 text-gray-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#112917] hover:text-white transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 text-gray-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#112917] hover:text-white transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO
        title="CSV Viewer | Jamil Global"
        description="Internal CSV deliverables viewer."
        url="/csvviewer"
        noindex
        nofollow
      />
      <style>{navbarOverrideStyles}</style>
      <div className="min-h-screen animate-slideIn">
        {/* First Section - Dark Blue */}
        {/* Hero Section */}
        <section className="relative min-h-screen bg-[#070c24] flex flex-col overflow-hidden">
          <div className="w-full relative">
            <div className="static">
              <nav className="relative">
                <Navbar noBlur />
              </nav>
            </div>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="container mx-auto px-4 text-center">
              <h1 className="text-4xl md:text-6xl text-white font-bold mb-6">
                CSV Viewer
              </h1>
              <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto">
                Explore our CSV Viewer and deliverables.
              </p>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute left-1/2 bottom-1 -translate-x-1/2 flex flex-col items-center gap-2">
            <div className="w-[1px] h-24 bg-white/20 relative overflow-hidden">
              <motion.div
                className="w-full h-full bg-white"
                animate={{ y: ["-100%", "100%"] }}
                transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
              />
            </div>
          </div>
        </section>

        {/* Second Section - White (Integrated CSV Table) */}
        <section className="min-h-screen bg-white py-20 px-8 flex flex-col items-center">
          <div className="container mx-auto">
            <h2 className="text-5xl text-gray-800 font-bold text-center mb-10">
              Our Key Deliverables
            </h2>

            {loading && (
              <div className="text-center text-lg text-gray-600">
                Loading deliverables data...
              </div>
            )}

            {error && (
              <div className="text-center text-lg text-red-600">
                Error: {error.message}. Please check your CSV file.
              </div>
            )}

            {!loading && !error && tableData.length === 0 && (
              <div className="text-center text-lg text-gray-600">
                No deliverable data found.
              </div>
            )}

            {!loading && !error && tableData.length > 0 && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-4">
                  <label className="text-gray-700">Rows per page:</label>
                  <select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-blue-300 rounded px-2 py-1 bg-blue-800 text-white cursor-pointer appearance-none focus:outline-none focus:border-pink-400 focus:ring-1 focus:ring-pink-400"
                    style={{
                      WebkitAppearance: "menulist",
                      MozAppearance: "menulist",
                    }}
                  >
                    {[5, 15, 30, 50, 100].map((value) => (
                      <option
                        key={value}
                        value={value}
                        className="bg-blue-900 text-white hover:bg-blue-100 hover:text-black"
                      >
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="overflow-x-auto shadow-xl rounded-lg border-2 border-blue-500">
                  <table className="min-w-full divide-y divide-white">
                    <thead className="bg-blue-400">
                      <tr>
                        {headers.map((header, index) => (
                          <th
                            key={index}
                            className="px-6 py-4 text-left text-xs font-semibold text-blue-100 uppercase tracking-wider border-b border-r last:border-r-0 border-blue-300"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-blue-900">
                      {currentRows.map((row, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className={
                            rowIndex % 2 === 0 ? "bg-blue-100" : "bg-white"
                          }
                        >
                          {headers.map((header, colIndex) => (
                            <td
                              key={`${rowIndex}-${colIndex}`}
                              className="px-6 py-4 text-sm text-gray-800 border-b border-r last:border-r-0 border-blue-200 break-words"
                            >
                              {row[header] !== null && row[header] !== undefined
                                ? row[header].toString()
                                : ""}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-between items-center mt-4">
                  <div className="text-sm text-gray-600">
                    Showing {indexOfFirstRow + 1} to{" "}
                    {Math.min(indexOfLastRow, tableData.length)} of{" "}
                    {tableData.length} entries
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-blue-900 text-blue-900 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-900 hover:text-white transition-colors"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-blue-900 text-blue-900 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-900 hover:text-white transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};

export default CSVViewer;
